<template>
  <div class="select-project-bom">
    <div class="level-selector">
      <div
        class="selector-tab"
        :class="{ active: selectedLevel === 'project' }"
        @click="changeLevel('project')"
      >
        Project BOM
      </div>
      <div
        class="selector-tab"
        :class="{ active: selectedLevel === 'master' }"
        @click="changeLevel('master')"
      >
        Master BOM
      </div>
    </div>
    <div v-if="selectedLevel === 'master'" class="product-code-filter-container input-group">
      <custom-dropdown
        :list="productCodeList"
        :selected="selectedProductCodeObj"
        :searchText="productCodeSearchText"
        default-label="All Product Codes"
        :default-style="dropdownStyleParams"
        @toggle="() => productCodeSearchText = ''"
        @select="onProductCodeSelect"
        @deselect="onProductCodeDeselect"
      >
        <template #before-items>
          <div class="fh center p-3">
            <input
              type="text"
              v-model="productCodeSearchText"
              placeholder="Search"
            />
          </div>
        </template>
      </custom-dropdown>
    </div>
    <div v-if="bomListLoading" class="select-project-bom-container loading">
      <loading-circle />
    </div>
    <div
      v-else-if="bomList.length === 0"
      class="select-project-bom-container no-bom-found"
    >
      <span class="text-muted">No BOM found</span>
    </div>
    <div v-else class="select-project-bom-container">
      <div
        :class="{
          selected: bom.id === selectedBom?.id,
          disable: !(bom.state === CHECK_IN  || bom.state === LOCK || bom.state === RELEASE )
        }"
        class="select-project-bom-option"
        v-for="bom in bomList"
        :key="bom.id"
        @click="selectBom(bom)"
        v-overflow-tooltip
      >
        <img
          v-if="getBomStateIcon(bom)"
          :src="getBomStateIcon(bom)"
          class="select-project-bom-option--icon"
        />
        {{ bom.name }}
      </div>
    </div>
    <div class="v-center flex-end m" v-if="showCloseButton">
      <button
        class="btn btn-black mt-3"
        @click="$emit('close')"
      >
        Close
      </button>
    </div>
  </div>
</template>

<script>
import { GetAllProjectBomList, GetAllBomList, GetAllProductCode } from '@/api'
import CustomDropdown from '@/components/common/customDropdown.vue'
import loadingCircle from '../../common/loadingCircle.vue'
import Config from '@/config'
import { mapGetters } from 'vuex'
export default {
  components: { loadingCircle, CustomDropdown },
  props: {
    selectedBomId: {
      type: [String, Number],
      default: null
    },
    showCloseButton: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedLevel: 'project',
      bomList: [],
      bomListLoading: false,
      selectedBom: null,
      LOCK: Config.BOM_STATE_MAP.LOCK,
      CHECK_IN: Config.BOM_STATE_MAP.CHECKIN,
      RELEASE: Config.BOM_STATE_MAP.RELEASE,
      productCodeList: [],
      selectedProductCodeObj: { id: null, name: 'All Product Codes' },
      productCodeSearchText: '',
      dropdownStyleParams: {
        backGroundColor: '#fff',
        borderColor: '#d1d5db',
        labelHeight: '36px',
        ddLabelFontSize: 'var(--text-s)'
      }
    }
  },
  computed: {
    ...mapGetters('projectBom', ['selectedProductCodeId']),
    filteredProductCodeList () {
      const keyword = this.productCodeSearchText?.trim()?.toLowerCase() || ''
      if (!keyword) return this.productCodeList
      return this.productCodeList.filter(item => item?.name?.toLowerCase().includes(keyword))
    }
  },
  methods: {
    getBomStateIcon (bom) {
      if (bom.state === 2) return require('@/assets/images/icons/checkIn-file.svg')
      if (bom.state === 3) return require('@/assets/images/icons/checkout-file.svg')
      if (bom.state === 1) return require('@/assets/images/icons/locked-icon.svg')
      if (bom.state === 5) return require('@/assets/images/icons/checkin-icon.svg')
      if (bom.state === 4) return require('@/assets/images/icons/obsolete-icon.svg')
      return null
    },
    getBomStateTooltip (bom) {
      if (bom.state === 2) return 'Bom is checked in'
      if (bom.state === 3) return 'Bom is checked out'
      if (bom.state === 1) return 'Bom is locked'
      if (bom.state === 5) return 'Bom is Released'
      if (bom.state === 4) return 'Bom is Obsolete'
      return ''
    },
    selectBom (bom) {
      if (this.selectedBom === bom.id) {
        this.selectedBom = null
        this.$emit('selectBom', {})
        return
      }
      this.selectedBom = bom.id
      this.$emit('selectBom', bom)
    },
    setPreselectedProductCode () {
      if (this.selectedProductCodeId && this.productCodeList.length) {
        const found = this.productCodeList.find(item => item.id != null && String(item.id) === String(this.selectedProductCodeId))
        if (found) {
          this.selectedProductCodeObj = found
        } else if (this.productCodeList.length) {
          this.selectedProductCodeObj = this.productCodeList[0]
        } else {
          this.selectedProductCodeObj = { id: null, name: 'All Product Codes' }
        }
      } else if (this.productCodeList.length) {
        this.selectedProductCodeObj = this.productCodeList[0]
      } else {
        this.selectedProductCodeObj = { id: null, name: 'All Product Codes' }
      }
    },
    changeLevel (level) {
      if (this.selectedLevel === level) return
      this.selectedLevel = level
      this.selectedBom = null
      this.setPreselectedProductCode()
      this.$emit('selectBom', {})
      this.getBomList()
    },
    onProductCodeSelect (item) {
      this.selectedProductCodeObj = item
      this.selectedBom = null
      this.$emit('selectBom', {})
      this.getBomList()
    },
    onProductCodeDeselect () {
      this.selectedProductCodeObj = { id: null, name: 'All Product Codes' }
      this.selectedBom = null
      this.$emit('selectBom', {})
      this.getBomList()
    },
    getProductCodes () {
      GetAllProductCode()
        .then((res) => {
          this.productCodeList = [
            ...(res?.product_code || [])
              .filter(item => item.linked_material?.status !== Config.MATERIAL_STATUS_INVERTED_MAP.OBSELETE)
              .map(item => ({
                id: item.id,
                name: item.product_code
              }))
          ]
          this.setPreselectedProductCode()
          if (this.selectedLevel === 'master') {
            this.getBomList()
          }
        })
        .catch((err) => {
          console.error(err)
        })
    },
    getBomList () {
      this.bomListLoading = true
      this.bomList = []
      if (this.selectedLevel === 'project') {
        GetAllProjectBomList(false, false, this.selectedProductCodeId)
          .then((res) => {
            this.bomList = res.core_bom.map((bom) => {
              return {
                id: bom.id,
                name: bom.name,
                product_code: bom.product_code,
                state: bom?.state,
                version: bom.bom_versions?.find((version) => version.active)?.version_no,
                versionId: bom.bom_versions?.find((version) => version.active)?.id,
                isProject: true
              }
            })
            this.bomListLoading = false
          })
          .catch(() => {
            this.bomListLoading = false
          })
      } else if (this.selectedProductCodeObj?.id) {
        GetAllBomList(this.selectedProductCodeObj?.id)
          .then((res) => {
            this.bomList = res.core_bom.map((bom) => {
              return {
                id: bom.id,
                name: bom.name,
                product_code: bom.product_code,
                state: bom?.state,
                version: bom.bom_versions?.find((version) => version.active)?.version_no,
                versionId: bom.bom_versions?.find((version) => version.active)?.id,
                isProject: false
              }
            })
            this.bomListLoading = false
          })
          .catch(() => {
            this.bomListLoading = false
          })
      } else {
        this.bomListLoading = false
      }
    }
  },
  created () {
    this.getBomList()
    this.selectedBom = this.selectedBomId
    this.getProductCodes()
  }
}
</script>

<style lang="scss" scoped >
.select-project-bom {
  width: 600px;
  margin: -10px;
  padding: 10px 10px;
  display: flex;
  flex-direction: column;
  max-height: 340px;
  min-height: 340px;

  .product-code-filter-container {
    margin-bottom: 12px;
    padding: 0 5px;
  }

  .level-selector {
    display: flex;
    justify-content: center;
    margin-bottom: 15px;
    background: rgba(var(--brand-rgb), 0.1);
    padding: 4px;
    border-radius: 8px;
    border: 1px solid rgba(var(--brand-rgb), 0.15);
  }

  .selector-tab {
    flex: 1;
    text-align: center;
    padding: 8px 16px;
    font-size: var(--text-s);
    font-weight: 500;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s ease-in-out;
    color: #475467;

    &:hover {
      background: rgba(var(--brand-rgb), 0.15);
    }

    &.active {
      background: var(--brand-color);
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 6px rgba(var(--brand-rgb), 0.3);
    }
  }

  &-container {
    overflow: auto;
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: center;
    grid-gap: 10px;
    &.no-bom-found {
      font-size: var(--text-l);
      align-content: center;
    }
    &.loading {
      align-content: center;
      align-items: center;
    }
  }
  &-option {
    position: relative;
    width: 120px;
    height: 40px;
    padding: 10px 10px;
    margin: 5px;
    border-radius: 8px;
    font-size: var(--text-s);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    cursor: pointer;
    text-align: center;
    background: rgba(var(--brand-rgb), 0.3);
    &.selected {
      background-color: rgba(var(--brand-rgb), 1) !important;
    }
    &.disable {
      background-color: rgba(var(--brand-rgb), 0.3) !important;
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.5;
    }
    &:hover {
      background-color: rgba(var(--brand-rgb), 0.7);
    }
    &--icon {
      position: absolute;
      top: 3px;
      right: 3px;
      width: 12px;
      height: 12px;
      z-index: 1;
    }
  }
}
</style>
